import React, { Fragment, useCallback, useState } from "react";
import { createRoot } from "react-dom/client";
import { useClickAway } from "./util/useClickAway";
import { BaseModal } from "./baseModal";

document.addEventListener("DOMContentLoaded", () => {
  const rootNode = document.querySelector("[data-ref=react-root]");
  const reactRoot = createRoot(rootNode);

  reactRoot.render(<ReservationPopup />);
});

type ModalViews =
  | "closed"
  | "initial"
  | "more-than-16"
  | "zenchef-reservation"
  | "exclusive-inquiry"
  | "duration-2.5-hours"
  | "option-1"
  | "option-2";

const ReservationPopup = () => {
  const [openState, setOpenState] = useState<ModalViews>("closed");
  const clickCallback = useCallback(
    (e: MouseEvent) => {
      if (
        (e.target as HTMLElement)?.dataset?.ref ===
        "open-reservation-popup-button"
      ) {
        setOpenState("initial");
      }
    },
    [openState],
  );

  const ref = useClickAway(clickCallback);

  const closePopup = useCallback(() => {
    setOpenState("closed");
  }, [openState]);

  const goToOpenState = useCallback(
    (newOpenState: ModalViews) => {
      setOpenState(newOpenState);
    },
    [openState],
  );

  const goToOption2 = useCallback(() => goToOpenState("option-2"), []);

  return (
    <div ref={ref}>
      {openState !== "closed" && (
        <Fragment>
          <div className="klinker-menu-backdrop"></div>
          {openState === "initial" && (
            <BaseModal
              modalTitle={undefined}
              onCloseClick={closePopup}
              buttons={[
                { text: "Ja", onClick: () => goToOpenState("more-than-16") },
                {
                  text: "Nein",
                  onClick: () => goToOpenState("zenchef-reservation"),
                },
              ]}
            >
              <h2 className="mb-3">Wie kann ich bei euch reservieren?</h2>
              <p>
                Unsere Öffnungszeiten sind von Montag bis Freitag ab 18 Uhr.
              </p>
              <hr />
              <p>
                Seid ihr <strong>mehr als 4 Personen?</strong>
              </p>
            </BaseModal>
          )}
          {openState === "zenchef-reservation" && (
            <BaseModal
              modalTitle={undefined}
              onCloseClick={closePopup}
              onBackNavigationClick={() => goToOpenState("initial")}
              buttons={[
                {
                  text: "Jetzt reservieren",
                  onClick: () => {
                    closePopup();

                    const currentUrl = location.href;
                    const newUrl = currentUrl
                      .split("#")?.[0]
                      .concat("#zc-action-open");

                    if (currentUrl.includes("#zc-action-open")) {
                      window.dispatchEvent(new HashChangeEvent("hashchange"));
                    } else {
                      location.href = newUrl;
                    }
                  },
                },
              ]}
            >
              <p>
                Wählt unter „Jetzt reservieren“ eure Wunschzeit aus oder kommt
                spontan zu uns!
              </p>
            </BaseModal>
          )}
          {openState === "more-than-16" && (
            <BaseModal
              modalTitle={undefined}
              onCloseClick={closePopup}
              onBackNavigationClick={() => goToOpenState("initial")}
              buttons={[
                {
                  text: "Ja",
                  onClick: () => goToOpenState("exclusive-inquiry"),
                },
                {
                  text: "Nein",
                  onClick: () => goToOpenState("duration-2.5-hours"),
                },
              ]}
            >
              <p>
                Seid ihr <strong>mehr als 16 Personen?</strong>
              </p>
            </BaseModal>
          )}
          {openState === "exclusive-inquiry" && (
            <BaseModal
              modalTitle={undefined}
              onCloseClick={closePopup}
              onBackNavigationClick={() => goToOpenState("more-than-16")}
              buttons={[
                {
                  text: "Kontakt",
                  onClick: () => {
                    closePopup();
                    location.href = "#exklusiv";
                  },
                },
              ]}
            >
              <p>
                Schreibt uns bezüglich einer <strong>Exklusivbuchung</strong>{" "}
                eine Nachricht über das untenstehende Kontaktformular mit
                Personenanzahl und Wunschdatum. Exklusivbuchungen bieten wir
                ausschließlich Samstag und Sonntag an.
              </p>
            </BaseModal>
          )}
          {openState === "duration-2.5-hours" && (
            <BaseModal
              modalTitle={undefined}
              onCloseClick={closePopup}
              onBackNavigationClick={() => goToOpenState("more-than-16")}
              buttons={[
                {
                  text: "Ja",
                  onClick: () => goToOpenState("option-1"),
                },
                {
                  text: "Nein",
                  onClick: () => goToOpenState("option-2"),
                },
              ]}
            >
              <p>
                Reicht euch ein <strong>Zeitfenster von 2,5 Stunden</strong> für
                eure Reservierung?
              </p>
            </BaseModal>
          )}
          {openState === "option-1" && (
            <BaseModal
              modalTitle="Option 1"
              onCloseClick={closePopup}
              onBackNavigationClick={() => goToOpenState("duration-2.5-hours")}
              buttons={[
                {
                  text: "Anfrage senden",
                  onClick: () => {
                    closePopup();
                    location.href =
                      "mailto:reservieren@restaurant-klinker.de?subject=Reservierungsanfrage";
                  },
                },
              ]}
            >
              <div className="text-start">
                <ul>
                  <li className="mb-4">
                    Reservierungen nehmen wir grundsätzlich maximal einen Monat
                    im Voraus an
                  </li>
                  <li className="mb-4">
                    Weil wir alle unsere Tische jeden Abend doppelt belegen,
                    gibt es von Montag bis Donnerstag für große Gruppen zwei
                    reguläre Zeitslots von 18:00 - 20:30 Uhr oder von 20:45 Uhr
                    bis Ende
                  </li>
                  <li className="mb-4">
                    Am Freitag gibt es für große Gruppen nur einen regulären
                    Zeitslot von 18:00 - 20:30 Uhr
                  </li>
                  <li className="mb-4">
                    Für Gruppen ab 5 Personen arbeiten wir mit einem festen
                    Sharing Menü für 70€ pro Person (zuzüglich Getränke)
                    bestehend aus 3 Runden à 2 Speisen, Brot und Butter sowie
                    Dessert
                  </li>
                  <li>
                    Die letzte Möglichkeit ist, dass ihr euch eine freie Zeit
                    aussucht, zu der ihr kommen wollt und den ganzen Abend
                    bleibt (s.{" "}
                    <button
                      className="bg-transparent border-0 p-0"
                      onClick={goToOption2}
                    >
                      Option 2
                    </button>
                    )
                  </li>
                </ul>
              </div>
            </BaseModal>
          )}
          {openState === "option-2" && (
            <BaseModal
              modalTitle="Option 2"
              onCloseClick={closePopup}
              onBackNavigationClick={() => goToOpenState("duration-2.5-hours")}
              buttons={[
                {
                  text: "Anfrage senden",
                  onClick: () => {
                    closePopup();
                    location.href =
                      "mailto:reservieren@restaurant-klinker.de?subject=Reservierungsanfrage";
                  },
                },
              ]}
            >
              <div className="text-start">
                <ul>
                  <li className="mb-4">
                    Reservierungen nehmen wir grundsätzlich maximal einen Monat
                    im Voraus an.
                  </li>
                  <li className="mb-4">
                    Für den Fall, dass ihr den ganzen Abend bei uns verbringen
                    wollt, müssen wir Euch mit einem Mindestumsatz von 110€
                    (freitags: 130€) pro Person berechnen
                  </li>
                  <li>
                    Für Gruppen ab 5 Personen arbeiten wir mit einem festen
                    Sharing Menü bestehend aus 3 Runden à 2 Speisen, Brot und
                    Butter sowie Dessert
                  </li>
                </ul>
              </div>
            </BaseModal>
          )}
        </Fragment>
      )}
    </div>
  );
};
